import { ActionOverRecordTypes, FieldBuilder, FieldDefinition, IActionsSchema, MENU_CATEGORY_KEY, MENU_NAME_SUFFIX, MENU_SUBCATEGORY_KEY, MenuCategories, MenuSubcategories, PERIOD_TYPE_NAME_PLANNED_PLANNING_SEASON, triggerAction, ZendraLocalizationServices } from '@zendra/shared';
import { PeriodService, PropertyService, ReasonService, RegionService, VendorService, ZoneService } from 'cbd-proxies';
import type { Property } from 'cbd-proxies';
import { OfficialPlannedPlantation } from '../official-planned-plantation/official-planned-plantation';

FieldBuilder.setNamespace('Operational');

type PropertyPrefilterQuery = Partial<Property.GetPropertiesInputDto> & {
  regionIds?: unknown;
  'location.name'?: string;
};

const isPositiveInteger = (value: number): boolean => Number.isInteger(value) && value > 0;

const parseEntityId = (value: string): number | null => {
  const parsedValue = Number(value);

  return isPositiveInteger(parsedValue) ? parsedValue : null;
};

const extractEntityIds = (rawValue: unknown): number[] => {
  if (typeof rawValue === 'number') {
    return isPositiveInteger(rawValue) ? [rawValue] : [];
  }

  if (typeof rawValue !== 'string') {
    return [];
  }

  return rawValue
    .split(',')
    .map(part => parseEntityId(part.trim()))
    .filter((parsedValue): parsedValue is number => parsedValue !== null);
};

const normalizeEntityIds = (value: unknown): number[] => {
  if (value === null || value === undefined || value === '') {
    return [];
  }

  const rawValues = Array.isArray(value) ? value : [value];

  return [...new Set(rawValues.flatMap(extractEntityIds))];
};

const loadOfficialPlannedPlantationHeaderVendors = (service: VendorService, query: any, config?: any) => {
  const {
    fantasyName,
    ...rest
  } = query ?? {};

  return service.getList(
    {
      ...rest,
      legalName: query?.legalName ?? fantasyName,
    },
    config,
  );
};

const loadOfficialPlannedPlantationHeaderProperties = (service: PropertyService, query: PropertyPrefilterQuery, config?: any) => {
  const regionIds = normalizeEntityIds(query?.regionIds);

  const {
    regionIds: _regionIds,
    regionId: _regionId,
    locationName,
    sorting,
    ['location.name']: nestedLocationName,
    ...rest
  } = query ?? {};

  const search = (locationName ?? nestedLocationName)?.trim();
  const requestedSorting = sorting?.trim() || 'location.name asc';
  const hasIdSorting = requestedSorting.split(',').some(term => /^id(?:\s+(?:asc|desc))?$/i.test(term.trim()));

  const propertyQuery: Property.GetPropertiesInputDto = {
    ...rest,
    skipCount: rest.skipCount ?? 0,
    maxResultCount: rest.maxResultCount ?? 20,
    ...(regionIds.length ? { regionId: regionIds } : {}),
    ...(search ? { locationName: search } : {}),
    sorting: hasIdSorting ? requestedSorting : `${requestedSorting}, id asc`,
    locationIsActive: true,
    isDisabled: false,
  };

  return service.getList(propertyQuery, config);
};

export const OFFICIALPLANNEDPLANTATIONHEADER_FIELDS: FieldDefinition[] = [
  FieldBuilder.foreignKey('zoneId', 'Entity.Property.Zone', ZoneService)
    .display('name').displayFromNested('zoneName').sortBy('zoneName').readOnly().build(),
  FieldBuilder.foreignKey('regionId', 'Entity.Region', RegionService)
    .display('name').displayFromNested('regionName').sortBy('regionName').readOnly().build(),
  FieldBuilder.text('regionCode', 'Entity.Region.Code').readOnly().build(),
  FieldBuilder.foreignKey(
    'periodId',
    'Entity.OfficialPlannedPlantationHeader.Period',
    PeriodService
  )
    .required()
    .queryFilter({ periodTypeName: PERIOD_TYPE_NAME_PLANNED_PLANNING_SEASON })
    .display('name')
    .displayFromNested('periodName')
    .sortBy('periodName')
    .keyField('id')
    .prefilter({ multiple: true, order: 2, enforceOnQueries: false })
    .build(),
  FieldBuilder.foreignKey(
    'vendorId',
    'Entity.Vendor',
    VendorService
  )
    .required()
    .display('fantasyName')
    .displayFromNested('vendorFantasyName')
    .sortBy('vendorFantasyName')
    .keyField('id')
    .loadWith((service, query, config) => loadOfficialPlannedPlantationHeaderVendors(service, query, config))
    .prefilter({ multiple: true, order: 1, enforceOnQueries: false })
    .build(),
  FieldBuilder.foreignKey(
    'regionPrefilterId',
    'Entity.Region',
    RegionService
  )
    .display('name')
    .keyField('id')
    .hideByDefault()
    .readOnly()
    .sortable(false)
    .filterable(false)
    .prefilter({ multiple: true, order: 3, enforceOnQueries: false })
    .build(),
  FieldBuilder.foreignKey(
    'propertyLocationId',
    'Entity.Property',
    PropertyService
  )
    .required()
    .display('location.name')
    .displayFromNested('propertyLocationName')
    .sortBy('propertyLocationName')
    .keyField('locationId')
    .loadWith((service, query, config) => loadOfficialPlannedPlantationHeaderProperties(service, query, config))
    .prefilter({ multiple: true, order: 4, dependsOn: { regionPrefilterId: 'regionIds' }, enforceOnQueries: false })
    .build(),
  FieldBuilder.foreignKey(
    'signatureReasonId',
    'Entity.OfficialPlannedPlantationHeader.SignatureReason',
    ReasonService
  )
    .required()
    .queryFilter({ reasonGroupName: 'Status Firma' })
    .display('name')
    .displayFromNested('signatureReasonName')
    .sortBy('signatureReasonName')
    .keyField('id')
    .build(),
  FieldBuilder.foreignKey(
    'dinamaReasonId',
    'Entity.OfficialPlannedPlantationHeader.DinamaReason',
    ReasonService
  )
    .required()
    .queryFilter({ reasonGroupName: 'Status Dinama' })
    .display('name')
    .displayFromNested('dinamaReasonName')
    .sortBy('dinamaReasonName')
    .keyField('id')
    .build(),
];

export const ACTION_IDS = {
  importExcelModal: 0,
  downloadExcelTemplateTrigger: 1,
  importStandsExcelModal: 2,
} as const;

export const OFFICIAL_PLANNED_PLANTATION_HEADER_ACTION_IDS = ACTION_IDS;

export const OFFICIALPLANNEDPLANTATIONHEADER_ACTIONS: IActionsSchema = {
  overTable: [
    triggerAction(
      ACTION_IDS.importExcelModal,
      'Entity.OfficialPlannedPlantationHeader.ImportPlanProperties',
      ActionOverRecordTypes.OverTable,
      ZendraLocalizationServices.Operational,
    ),
    triggerAction(
      ACTION_IDS.importStandsExcelModal,
      'Entity.OfficialPlannedPlantationHeader.ImportPlanStands',
      ActionOverRecordTypes.OverTable,
      ZendraLocalizationServices.Operational,
    ),
    triggerAction(
      ACTION_IDS.downloadExcelTemplateTrigger,
      'Actions:ExcelTemplatesDownload',
      ActionOverRecordTypes.OverTable,
      ZendraLocalizationServices.LanguageService,
    )
  ],
};

export const OFFICIAL_PLANNED_PLANTATION_HEADER_ACTIONS_CONFIG = OFFICIALPLANNEDPLANTATIONHEADER_ACTIONS;

export const OFFICIALPLANNEDPLANTATIONHEADER_CONFIG = {
  entityName: 'OfficialPlannedPlantationHeader',
  localizationKey: 'Operational::Entity.OfficialPlannedPlantationHeader',
  fields: OFFICIALPLANNEDPLANTATIONHEADER_FIELDS,
  permission: 'PlanningOperation.OfficialPlannedPlantationHeaders',
  showInMenu: true,
  menuCategory: MENU_CATEGORY_KEY + MenuCategories.PlanningOperational + MENU_NAME_SUFFIX,
  menuSubcategory: MENU_SUBCATEGORY_KEY + MenuSubcategories.Silviculture + MENU_NAME_SUFFIX,
  actions: OFFICIAL_PLANNED_PLANTATION_HEADER_ACTION_IDS,
  actionIds: ACTION_IDS,
  children: [
    {
      entityName: 'OfficialPlannedPlantation',
      foreignKeyField: 'officialPlannedPlantationHeaderId',
      localizationKey: 'Operational::Entity.OfficialPlannedPlantation',
      component: OfficialPlannedPlantation,
    }
  ],
};
